// Providers
import { getBlob, getArtifactsByMessageId } from '../../services/artifact.service';
// Enums
import { ArtifactType, SupportedMimeType } from '../../enums';
// Interfaces
import { IArtifact } from '../../interfaces/artifact.interfaces';
// Helpers
import { isIOS } from '../../helpers/browser';

const printStyle =
    '@media print { @page { size: auto; margin: 0; border: 0 !important; } body { margin: 0 1.6cm 0 1.6cm; padding: 1.6cm 0 1.6cm 0; border: 0 !important; } }';
const iosStyle = 'width: 1px; height: 1px; min-height: 100%; min-width: 100%; ';
const defaultStyle = 'width: 100%; height: 100%; ';
let newWindow: Window = null;
let tabTitle: string = null;
let isiOS: boolean;

export function PrintHtml(mailboxId: string, documentId: string, artifactId: string, fileId: string): void {
    printHtmlframe(mailboxId, documentId, artifactId, fileId);
}

export function PrintText(mailboxId: string, documentId: string, artifactId: string, fileId: string): void {
    printTextWrapper(mailboxId, documentId, artifactId, fileId);
}

export function printPdf(mailboxId: string, documentId: string, artifactId: string, fileId: string): void {
    getBlob(mailboxId, documentId, artifactId, fileId, SupportedMimeType.PDF).then((content) => {
        const file = new Blob([content], { type: SupportedMimeType.PDF });
        const fileUrl = URL.createObjectURL(file);
        pdfPrint(fileUrl);
    });
}

export function printArtifact(mailboxId: string, artifact: IArtifact, title: string): void {
    newWindow = window.open('', '_blank');
    isiOS = isIOS();
    tabTitle = title;

    switch (artifact.files[0].encodingFormat) {
        case 'application/pdf':
            printPdf(mailboxId, artifact.messageId, artifact.id, artifact.files[0].id);
            break;

        case 'text/plain':
            PrintText(mailboxId, artifact.messageId, artifact.id, artifact.files[0].id);
            break;

        case 'text/html':
            PrintHtml(mailboxId, artifact.messageId, artifact.id, artifact.files[0].id);
            break;
    }
}

export function printDocument(mailboxId: string, documentId: string, language: string, title: string): void {
    getArtifactsByMessageId(mailboxId, documentId, language, true).then((res) => {
        const mainArtifact = res.items.find((artifact) => {
            return artifact.type === ArtifactType.MESSAGE_BODY_DOCUMENT;
        });
        if (mainArtifact) {
            printArtifact(mailboxId, mainArtifact, title);
        }
    });
}

function getPrintIFrame(frameWindow: Window): HTMLIFrameElement {
    const printIframe = frameWindow.document.createElement('iframe');
    frameWindow.document.body.appendChild(printIframe);
    printIframe.frameBorder = '0';
    printIframe.marginHeight = '0';
    printIframe.marginWidth = '0';
    printIframe.scrolling = 'no';
    printIframe.width = '1px';
    printIframe.height = '1px';
    printIframe.style.minWidth = '100%';
    printIframe.style.minHeight = '100%';
    printIframe.setAttribute('style', isiOS ? iosStyle + printStyle : defaultStyle + printStyle);
    printIframe.id = 'printIframe';
    return printIframe;
}

function printHtmlframe(mailboxId: string, documentId: string, artifactId: string, fileId: string): void {
    const printFrame = getPrintIFrame(newWindow);
    let divWrap: HTMLDivElement;

    if (isiOS) {
        divWrap = getDivElement(newWindow);
        newWindow.document.body.appendChild(divWrap);
    }

    getBlob(mailboxId, documentId, artifactId, fileId, SupportedMimeType.HTML).then((content) => {
        const file = new Blob([content], { type: SupportedMimeType.HTML });
        const fileUrl = URL.createObjectURL(file);
        if (isiOS) {
            divWrap.appendChild(printFrame);
        }
        printFrame.onload = (): void => {
            setTimeout(() => focusPrintFrame(printFrame, fileUrl), 1000);
        };
        printFrame.src = fileUrl;
    });
}

function printTextWrapper(mailboxId: string, documentId: string, artifactId: string, fileId: string): void {
    getBlob(mailboxId, documentId, artifactId, fileId, SupportedMimeType.TXT).then((res) => {
        const reader = new FileReader();
        reader.addEventListener('loadend', (e) => {
            const text = e.target.result;
            const printFrame = getPrintIFrame(newWindow);
            printFrame.setAttribute('style', defaultStyle + printStyle);
            const contentWindow = printFrame.contentWindow;
            contentWindow.document.open();
            contentWindow.document.write('<pre id="preTextToPrint">' + text + '</pre>');
            contentWindow.document.close();
            contentWindow.focus();
            newWindow.document.title = tabTitle;
            contentWindow.print();
        });
        reader.readAsText(res, 'UTF-8');
    });
}

function getDivElement(divWindow: Window): HTMLDivElement {
    const divWrap = divWindow.document.createElement('div');
    divWrap.tabIndex = -1;
    divWrap.setAttribute('style', 'overflow: auto; -webkit-overflow-scrolling:touch; width: 100%; height: 100%;');
    divWrap.id = 'divWrap';
    return divWrap;
}

function pdfPrint(fileUrl: string): void {
    if (isiOS) {
        newWindow.document.write(
            '<div id="divWrap" tabindex="-1" style="overflow: auto; -webkit-overflow-scrolling:touch; width: 100%; height: 100%;"><iframe id="printFrame" style="' +
                iosStyle +
                printStyle +
                '" src="' +
                fileUrl +
                '" width="100%" height="100%" frameborder="0" marginheight="0" marginwidth="0" scrolling="no"></div>'
        );
    } else {
        newWindow.document.write(
            '<iframe id="printFrame" style="' +
                defaultStyle +
                printStyle +
                '" src="' +
                fileUrl +
                '" width="100%" height="100%" frameborder="0" marginheight="0" marginwidth="0" scrolling="no">'
        );
    }

    newWindow.document.close();

    const load = (): void => {
        const printFrame = newWindow.document.getElementById('printFrame') as HTMLIFrameElement;
        setTimeout(() => focusPrintFrame(printFrame, fileUrl), 1000);
    };
    newWindow.onload = load;
}

const focusPrintFrame = (printFrame: HTMLIFrameElement, url: string): void => {
    printFrame.focus();
    newWindow.document.title = tabTitle;
    printFrame.contentWindow.print();
    URL.revokeObjectURL(url);
};
